
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Home() {
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.language);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // SEO Meta Tags
  useEffect(() => {
    document.title = 'GüvenTar Sulama Sistemleri - Damla Sulama ve Tarımsal Sulama Çözümleri | Antalya Serik';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'GüvenTar Sulama Sistemleri, Antalya Serik merkezli damla sulama sistemleri kurulumu, tarımsal sulama projelendirme ve otomasyon çözümleri sunan uzman firma. 10+ yıl tecrübe, 500+ başarılı proje.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'damla sulama, tarımsal sulama, sulama sistemleri, Antalya, Serik, GüvenTar Sulama');
    }

    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://www.guventarsulama.com'}`);
    }

    // Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "GüvenTar Sulama Sistemleri",
      "description": "Damla sulama sistemleri kurulumu ve tarımsal sulama çözümleri",
      "url": import.meta.env.VITE_SITE_URL || 'https://www.guventarsulama.com',
      "telephone": "+90 553 414 52 63",
      "email": "info@guventarsulama.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Cumalı Mh. Antalya Cd. No:9",
        "addressLocality": "Serik",
        "addressRegion": "Antalya",
        "postalCode": "07500",
        "addressCountry": "TR"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "36.8765",
        "longitude": "31.0123"
      },
      "openingHours": [
        "Mo-Sa 08:00-18:00"
      ],
      "priceRange": "$$",
      "serviceArea": {
        "@type": "Country",
        "name": "Turkey"
      },
      "sameAs": [
        "https://wa.me/905534145263"
      ]
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const toggleLanguage = () => {
    const newLang = language === 'tr' ? 'en' : 'tr';
    setLanguage(newLang);
    i18n.changeLanguage(newLang);
  };

  // Hero görsel rotasyonu için
  const heroImages = [
    {
      url: "https://readdy.ai/api/search-image?query=Modern%20drip%20irrigation%20system%20in%20green%20agricultural%20field%20with%20water%20droplets%2C%20professional%20farming%20technology%2C%20sustainable%20agriculture%2C%20clean%20background%20with%20blue%20sky%20and%20green%20plants%2C%20high%20quality%20agricultural%20equipment%20installation&width=1920&height=1080&seq=hero-irrigation-1&orientation=landscape",
      title: t('hero.slides.slide1.title'),
      subtitle: t('hero.slides.slide1.subtitle')
    },
    {
      url: "https://readdy.ai/api/search-image?query=Beautiful%20vineyard%20with%20precision%20irrigation%20system%20at%20golden%20sunset%2C%20rows%20of%20grapevines%20with%20modern%20drip%20irrigation%20technology%2C%20warm%20golden%20light%20filtering%20through%20leaves%2C%20professional%20agricultural%20landscape%20with%20purple%20and%20orange%20sky%20colors&width=1920&height=1080&seq=hero-irrigation-new&orientation=landscape",
      title: t('hero.slides.slide2.title'),
      subtitle: t('hero.slides.slide2.subtitle')
    },
    {
      url: "https://readdy.ai/api/search-image?query=Smart%20agricultural%20field%20with%20automated%20irrigation%20technology%2C%20green%20crops%20with%20precision%20watering%20system%2C%20modern%20sustainable%20farming%20with%20blue%20and%20green%20colors%2C%20high-tech%20agricultural%20equipment&width=1920&height=1080&seq=hero-irrigation-3&orientation=landscape",
      title: t('hero.slides.slide3.title'),
      subtitle: t('hero.slides.slide3.subtitle')
    },
    {
      url: "https://readdy.ai/api/search-image?query=Professional%20greenhouse%20irrigation%20system%20with%20mist%20sprayers%2C%20controlled%20environment%20agriculture%2C%20modern%20hydroponic%20farming%20technology%2C%20clean%20white%20and%20green%20design%20with%20water%20droplets&width=1920&height=1080&seq=hero-irrigation-4&orientation=landscape",
      title: t('hero.slides.slide4.title'),
      subtitle: t('hero.slides.slide4.subtitle')
    }
  ];

  // 4 saniyede bir görsel değiştir
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === heroImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000);

    return () => clearInterval(interval);
  }, [heroImages.length]);

  const features = [
    {
      icon: 'ri-drop-line',
      title: t('features.efficiency.title'),
      description: t('features.efficiency.desc')
    },
    {
      icon: 'ri-plant-line',
      title: t('features.sustainable.title'),
      description: t('features.sustainable.desc')
    },
    {
      icon: 'ri-settings-3-line',
      title: t('features.reliable.title'),
      description: t('features.reliable.desc')
    }
  ];

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: t('values.trust.title'),
      description: t('values.trust.desc')
    },
    {
      icon: 'ri-award-line',
      title: t('values.quality.title'),
      description: t('values.quality.desc')
    },
    {
      icon: 'ri-customer-service-2-line',
      title: t('values.satisfaction.title'),
      description: t('values.satisfaction.desc')
    },
    {
      icon: 'ri-leaf-line',
      title: t('values.nature.title'),
      description: t('values.nature.desc')
    }
  ];

  const stats = [
    { number: '500+', label: t('stats.projects') },
    { number: '25+', label: t('stats.experience') },
    { number: '50+', label: t('stats.customers') },
    { number: '24/7', label: t('stats.support') }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      <header>
        <h1 style={{ position: 'absolute', left: '-9999px' }}>GüvenTar Sulama Sistemleri - Damla Sulama ve Tarımsal Sulama Çözümleri</h1>
      </header>

      {/* Language Toggle Button - Hidden on Mobile */}
      <div className="fixed top-4 sm:top-6 right-4 sm:right-6 z-50 hidden sm:block">
        <button
          onClick={toggleLanguage}
          className="group relative flex items-center space-x-2 sm:space-x-3 bg-white/90 backdrop-blur-md text-gray-700 px-4 sm:px-6 py-2 sm:py-3 rounded-full border border-gray-200/50 hover:bg-white hover:shadow-lg transition-all duration-300 cursor-pointer whitespace-nowrap"
        >
          <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-green-800 to-green-900 rounded-full flex items-center justify-center shadow-sm">
            <i className="ri-global-line text-white text-sm sm:text-lg"></i>
          </div>
          <div className="hidden sm:flex flex-col items-start">
            <span className="text-xs text-gray-500 font-medium">{t('language.label')}</span>
            <span className="text-sm font-semibold text-gray-800">
              {t('language.switch')}
            </span>
          </div>
          <span className="sm:hidden text-sm font-semibold text-gray-800">
            {language === 'tr' ? 'EN' : 'TR'}
          </span>
          <i className="ri-arrow-down-s-line text-gray-400 group-hover:text-gray-600 transition-colors hidden sm:block"></i>
        </button>
      </div>

      {/* Phone Call Button */}
      <a
        href="tel:+905534145263"
        className="fixed right-4 sm:right-6 bottom-20 sm:bottom-24 w-12 h-12 sm:w-14 sm:h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 z-40 cursor-pointer"
        title="Hemen Ara"
      >
        <i className="ri-phone-line text-lg sm:text-xl"></i>
      </a>

      {/* WhatsApp Button */}
      <a
        href="https://wa.me/905534145263"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed right-4 sm:right-6 bottom-4 sm:bottom-6 w-12 h-12 sm:w-14 sm:h-14 bg-green-800 hover:bg-green-900 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 z-40 cursor-pointer"
        title="WhatsApp"
      >
        <i className="ri-whatsapp-line text-lg sm:text-xl"></i>
      </a>
      
      {/* Main Content with Left Margin for Desktop */}
      <div className="lg:ml-80">
        {/* Hero Section with Rotating Images */}
        <section 
          className="relative h-screen flex items-center justify-center bg-cover bg-center transition-all duration-1000"
          style={{
            backgroundImage: `linear-gradient(rgba(27, 94, 32, 0.6), rgba(27, 94, 32, 0.6)), url('${heroImages[currentImageIndex].url}')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-green-900/50 to-transparent"></div>
          <div className="relative text-center text-white px-4 max-w-5xl mx-auto">
            <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 sm:mb-8 leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {heroImages[currentImageIndex].title}
            </h2>
            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl mb-8 sm:mb-12 font-light leading-relaxed">
              {heroImages[currentImageIndex].subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
              <Link 
                to="/hakkimizda"
                className="inline-block bg-white/20 backdrop-blur-sm text-white px-6 sm:px-10 py-3 sm:py-5 rounded-2xl font-semibold text-base sm:text-lg hover:bg-white/30 transition-all duration-300 cursor-pointer whitespace-nowrap border border-white/30"
              >
                <i className="ri-information-line mr-2"></i>
                {t('hero.cta.about')}
              </Link>
              <Link 
                to="/iletisim"
                className="inline-block bg-green-800 text-white px-6 sm:px-10 py-3 sm:py-5 rounded-2xl font-semibold text-base sm:text-lg hover:bg-green-700 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-xl"
              >
                <i className="ri-phone-line mr-2"></i>
                {t('hero.cta.contact')}
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 sm:py-16 lg:py-20 bg-white/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-green-800 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {stat.number}
                  </div>
                  <div className="text-gray-600 font-medium text-sm sm:text-base">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-12 sm:py-16 lg:py-20 bg-gray-50/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 lg:mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 lg:mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {t('features.title')}
              </h2>
              <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                {t('features.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {features.map((feature, index) => (
                <div key={index} className="text-center bg-white/60 backdrop-blur-sm p-6 sm:p-8 lg:p-10 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-white/20">
                  <div className="w-16 h-16 sm:w-18 sm:h-18 lg:w-20 lg:h-20 bg-gradient-to-br from-green-800 to-green-900 rounded-2xl flex items-center justify-center mx-auto mb-6 lg:mb-8 shadow-lg">
                    <i className={`${feature.icon} text-3xl lg:text-4xl text-white`}></i>
                  </div>
                  <h3 className="text-xl lg:text-2xl font-semibold mb-4 lg:mb-6 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-base lg: text-lg">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-12 sm:py-16 lg:py-20 bg-white/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 lg:mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 lg:mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {t('values.title')}
              </h2>
              <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                {t('values.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {values.map((value, index) => (
                <div key={index} className="text-center group hover:scale-105 transition-transform duration-300">
                  <div className="w-20 h-20 sm:w-22 sm:h-22 lg:w-24 lg:h-24 bg-gradient-to-br from-green-800 to-green-900 rounded-3xl flex items-center justify-center mx-auto mb-6 lg:mb-8 shadow-xl group-hover:shadow-2xl transition-shadow duration-300">
                    <i className={`${value.icon} text-3xl lg:text-4xl text-white`}></i>
                  </div>
                  <h3 className="text-lg lg:text-xl font-semibold mb-3 lg:mb-4 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm lg:text-base">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        <section className="py-12 sm:py-16 lg:py-20 bg-gray-50/80 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 lg:mb-16">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 lg:mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {t('gallery.title')}
              </h2>
              <p className="text-lg lg:text-xl text-gray-600">
                {t('gallery.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 group">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20team%20installing%20drip%20irrigation%20system%20in%20agricultural%20field%2C%20workers%20in%20safety%20equipment%2C%20modern%20farming%20technology%20installation%2C%20green%20agricultural%20landscape%20with%20irrigation%20pipes%20and%20equipment&width=400&height=300&seq=gallery-1&orientation=landscape"
                  alt="Sulama sistemi kurulumu"
                  className="w-full h-60 sm:h-72 lg:h-80 object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 text-white">
                  <h3 className="text-lg lg:text-xl font-semibold mb-1 lg:mb-2">
                    {t('gallery.installation')}
                  </h3>
                  <p className="text-xs sm:text-sm opacity-90">
                    {t('gallery.installation.desc')}
                  </p>
                </div>
              </div>

              <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 group">
                <img 
                  src="https://readdy.ai/api/search-image?query=Modern%20agricultural%20field%20with%20drip%20irrigation%20system%20working%2C%20water%20droplets%20on%20plants%2C%20green%20crops%20growing%20with%20irrigation%20technology%2C%20sustainable%20farming%20practices%2C%20professional%20agricultural%20equipment&width=400&height=300&seq=gallery-2&orientation=landscape"
                  alt="Çalışan sulama sistemi"
                  className="w-full h-60 sm:h-72 lg:h-80 object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 text-white">
                  <h3 className="text-lg lg:text-xl font-semibold mb-1 lg:mb-2">
                    {t('gallery.active')}
                  </h3>
                  <p className="text-xs sm:text-sm opacity-90">
                    {t('gallery.active.desc')}
                  </p>
                </div>
              </div>

              <div className="relative overflow-hidden rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 group md:col-span-3 lg:col-span-1">
                <img 
                  src="https://readdy.ai/api/search-image?query=Engineering%20team%20planning%20irrigation%20project%20with%20blueprints%20and%20technical%20drawings%2C%20professional%20consultation%20meeting%2C%20agricultural%20engineering%20office%2C%20modern%20technology%20planning%20for%20farming%20irrigation%20systems&width=400&height=300&seq=gallery-3&orientation=landscape"
                  alt="Proje planlama"
                  className="w-full h-60 sm:h-72 lg:h-80 object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 text-white">
                  <h3 className="text-lg lg:text-xl font-semibold mb-1 lg:mb-2">
                    {t('gallery.planning')}
                  </h3>
                  <p className="text-xs sm:text-sm opacity-90">
                    {t('gallery.planning.desc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section 
          className="py-12 sm:py-16 lg:py-20 relative bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(27, 94, 32, 0.9), rgba(27, 94, 32, 0.9)), url('https://readdy.ai/api/search-image?query=Beautiful%20agricultural%20landscape%20with%20irrigation%20systems%20at%20sunset%2C%20golden%20hour%20lighting%20on%20green%20fields%2C%20modern%20farming%20technology%20in%20peaceful%20rural%20setting%2C%20professional%20agricultural%20equipment&width=1920&height=600&seq=cta-bg&orientation=landscape')`
          }}
        >
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 lg:mb-8" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {t('cta.title')}
            </h2>
            <p className="text-lg sm:text-xl lg:text-2xl text-green-100 mb-8 lg:mb-12 leading-relaxed">
              {t('cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
              <Link 
                to="/iletisim"
                className="inline-block bg-white text-green-800 px-6 sm:px-10 py-3 sm:py-5 rounded-2xl font-semibold text-base sm:text-lg hover:bg-gray-100 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-xl"
              >
                <i className="ri-phone-line mr-2"></i>
                {t('cta.contact')}
              </Link>
              <Link 
                to="/hizmet-alanlari"
                className="inline-block bg-white/20 backdrop-blur-sm text-white px-6 sm:px-10 py-3 sm:py-5 rounded-2xl font-semibold text-base sm:text-lg hover:bg-white/30 transition-all duration-300 cursor-pointer whitespace-nowrap border border-white/30"
              >
                <i className="ri-service-line mr-2"></i>
                {t('cta.services')}
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </div>
  );
}
